import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { 
  FaBriefcase, 
  FaEnvelope, 
  FaArrowLeft, 
  FaUsers, 
  FaLightbulb, 
  FaRocket,
  FaHeart,
  FaGlobe,
  FaCode
} from 'react-icons/fa';
import './Careers.css';

const Careers = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: "easeOut"
      }
    }
  };

  const handleUnsolicitedApplication = () => {
    const subject = "Unsolicited Application - [Your Name]";
    const body = `
Dear Infinity Logs Team,

I am writing to express my interest in potential career opportunities at Infinity Logs. Although there are currently no open positions listed, I would like to submit my unsolicited application for future consideration.

Please find my details below:

Name: [Your Full Name]
Email: [Your Email]
Phone: [Your Phone Number]
LinkedIn: [Your LinkedIn Profile]
Portfolio: [Your Portfolio/Website]

Current Position: [Your Current Job Title]
Years of Experience: [X years]

Why I'm interested in Infinity Logs:
[Write a brief paragraph about why you want to work at Infinity Logs]

Skills & Expertise:
[List your relevant skills]

I have attached my resume and would be grateful for the opportunity to discuss how my experience and passion for technology could contribute to your team's success.

Thank you for your time and consideration.

Best regards,
[Your Name]
    `;
    
    const mailtoLink = `mailto:info@infinitylogs.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoLink;
  };

  const companyValues = [
    {
      icon: <FaLightbulb />,
      title: "Innovation",
      description: "We encourage creative thinking and innovative solutions to complex problems."
    },
    {
      icon: <FaUsers />,
      title: "Collaboration",
      description: "Teamwork makes the dream work. We value diverse perspectives and open communication."
    },
    {
      icon: <FaRocket />,
      title: "Growth",
      description: "Continuous learning and professional development are at the core of our culture."
    },
    {
      icon: <FaHeart />,
      title: "Passion",
      description: "We love what we do and are passionate about creating exceptional digital experiences."
    }
  ];

  return (
    <div className="careers-page">
      <Header />
      <div className="container">
        {/* Header */}
        <motion.div
          className="careers-header"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <Link to="/">
            <motion.div 
              className="back-link"
              whileHover={{ x: -5 }}
            >
              <FaArrowLeft />
              Back to Home
            </motion.div>
          </Link>
          
          <div className="header-content">
            <motion.div 
              className="header-icon"
              animate={{ 
                rotate: [0, 5, -5, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <FaBriefcase />
            </motion.div>
            <h1 className="page-title">Careers at Infinity Logs</h1>
            <p className="page-subtitle">
              Join our team of passionate developers and designers creating the future of digital solutions.
            </p>
          </div>
        </motion.div>

        {/* No Jobs Available Section */}
        <motion.div
          className="no-jobs-section"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div className="no-jobs-content" variants={itemVariants}>
            <div className="no-jobs-icon">
              <FaBriefcase />
            </div>
            <h2>No Open Positions</h2>
            <p className="no-jobs-message">
              We currently don't have any open positions, but we're always looking for exceptional talent 
              to join our growing team. Great companies are built by great people, and we believe in 
              finding the right fit for our culture and values.
            </p>
            <p className="no-jobs-submessage">
              Check back regularly as we frequently add new opportunities. In the meantime, 
              feel free to send us your unsolicited application.
            </p>
          </motion.div>

          {/* Unsolicited Application CTA */}
          <motion.div className="unsolicited-section" variants={itemVariants}>
            <div className="unsolicited-content">
              <h3>Don't See a Role That Fits?</h3>
              <p>
                We're always interested in hearing from talented individuals who share our passion for 
                technology and innovation. Send us your unsolicited application and we'll keep your 
                information on file for future opportunities.
              </p>
              <motion.button
                className="unsolicited-btn"
                onClick={handleUnsolicitedApplication}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaEnvelope />
                Send Unsolicited Application
              </motion.button>
            </div>
          </motion.div>
        </motion.div>

        {/* Why Work With Us */}
        <motion.div
          className="why-work-section"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          <motion.div className="section-header" variants={itemVariants}>
            <h2>Why Work With Us?</h2>
            <p>Discover what makes Infinity Logs a great place to work and grow your career.</p>
          </motion.div>

          <div className="values-grid">
            {companyValues.map((value, index) => (
              <motion.div
                key={index}
                className="value-card"
                variants={itemVariants}
                whileHover={{ y: -10, scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div className="value-icon">{value.icon}</div>
                <h3 className="value-title">{value.title}</h3>
                <p className="value-description">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* What We're Looking For */}
        <motion.div
          className="looking-for-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <div className="looking-for-content">
            <h2>What We're Looking For</h2>
            <div className="requirements-grid">
              <div className="requirement-category">
                <h3>Technical Skills</h3>
                <ul>
                  <li>React, Node.js, and modern JavaScript</li>
                  <li>Mobile development (React Native, Flutter)</li>
                  <li>Cloud platforms and DevOps</li>
                  <li>UI/UX design principles</li>
                  <li>Database design and optimization</li>
                </ul>
              </div>
              <div className="requirement-category">
                <h3>Soft Skills</h3>
                <ul>
                  <li>Strong communication and collaboration</li>
                  <li>Problem-solving mindset</li>
                  <li>Adaptability and continuous learning</li>
                  <li>Attention to detail and quality</li>
                  <li>Passion for technology and innovation</li>
                </ul>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Benefits */}
        <motion.div
          className="benefits-section"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
        >
          <div className="benefits-content">
            <h2>Benefits & Perks</h2>
            <div className="benefits-grid">
              <div className="benefit-item">
                <FaCode className="benefit-icon" />
                <span>Cutting-edge technology stack</span>
              </div>
              <div className="benefit-item">
                <FaUsers className="benefit-icon" />
                <span>Collaborative team environment</span>
              </div>
              <div className="benefit-item">
                <FaRocket className="benefit-icon" />
                <span>Professional development opportunities</span>
              </div>
              <div className="benefit-item">
                <FaGlobe className="benefit-icon" />
                <span>Flexible work arrangements</span>
              </div>
              <div className="benefit-item">
                <FaHeart className="benefit-icon" />
                <span>Health and wellness programs</span>
              </div>
              <div className="benefit-item">
                <FaLightbulb className="benefit-icon" />
                <span>Innovation time and side projects</span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Contact CTA */}
        <motion.div
          className="contact-cta"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="cta-content">
            <h3>Ready to Join Our Team?</h3>
            <p>
              Even though we don't have open positions right now, we'd love to hear from you. 
              Send us your application and let's start a conversation about your future at Infinity Logs.
            </p>
            <motion.button
              className="cta-btn"
              onClick={handleUnsolicitedApplication}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaEnvelope />
              Send Your Application
            </motion.button>
          </div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default Careers;
